import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api, { adminAPI } from '../../lib/api';
import { 
  Gift, Plus, Loader2, RefreshCw, Search, DollarSign,
  Mail, User, Calendar, Check, X, CreditCard, Send, Share2, 
  MessageCircle, Image, Palette, Type, Percent, Tag, Sparkles,
  Download, Eye, Edit2, Upload, Trash2, Phone, MessageSquare
} from 'lucide-react';

// Promo Gift Card Templates
const PROMO_TEMPLATES = [
  {
    id: 'free_tour',
    name: 'FREE TOUR',
    headline: 'FREE TOUR',
    subline: 'Experience Puerto Rico',
    bgGradient: 'from-emerald-500 via-teal-500 to-cyan-500',
    accentColor: '#10b981',
    textColor: 'white',
    icon: Gift
  },
  {
    id: '50_off',
    name: '50% OFF',
    headline: '50% OFF',
    subline: 'Your Next Adventure',
    bgGradient: 'from-orange-500 via-red-500 to-pink-500',
    accentColor: '#f97316',
    textColor: 'white',
    icon: Percent
  },
  {
    id: 'bogo',
    name: 'BOGO',
    headline: 'BUY ONE GET ONE',
    subline: 'FREE',
    bgGradient: 'from-purple-500 via-violet-500 to-indigo-500',
    accentColor: '#8b5cf6',
    textColor: 'white',
    icon: Tag
  },
  {
    id: 'gift_card',
    name: 'Gift Card',
    headline: 'GIFT CARD',
    subline: 'Tour Finder Puerto Rico',
    bgGradient: 'from-amber-400 via-yellow-500 to-orange-400',
    accentColor: '#f59e0b',
    textColor: 'white',
    icon: CreditCard
  },
  {
    id: 'special',
    name: 'Special Offer',
    headline: 'SPECIAL OFFER',
    subline: 'Limited Time Only',
    bgGradient: 'from-rose-500 via-pink-500 to-fuchsia-500',
    accentColor: '#ec4899',
    textColor: 'white',
    icon: Sparkles
  }
];

// Gift Card Preview Component
const GiftCardPreview = React.forwardRef(({ template, customText, amount, recipientName, senderName, code, customImage }, ref) => {
  const IconComponent = template?.icon || Gift;
  
  // If custom image is provided, use it as background
  if (customImage) {
    return (
      <div 
        ref={ref}
        className="relative w-full aspect-[1.6/1] rounded-2xl shadow-2xl overflow-hidden"
        style={{ 
          minHeight: '220px',
          backgroundImage: `url(${customImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-black/30" />
        
        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-between text-white p-6">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Gift className="h-6 w-6" />
                <span className="text-sm font-medium opacity-90">TOUR FINDER</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight drop-shadow-lg">
                {customText?.headline || 'GIFT CARD'}
              </h2>
              <p className="text-lg font-semibold opacity-90 mt-1 drop-shadow">
                {customText?.subline || 'Puerto Rico Tours'}
              </p>
            </div>
            {amount && (
              <div className="text-right bg-black/40 px-3 py-2 rounded-lg">
                <div className="text-3xl md:text-4xl font-black">${amount}</div>
                <div className="text-sm opacity-80">Value</div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex justify-between items-end">
            <div className="bg-black/40 px-3 py-2 rounded-lg">
              {recipientName && (
                <p className="text-sm opacity-90">To: <span className="font-semibold">{recipientName}</span></p>
              )}
              {senderName && (
                <p className="text-sm opacity-90">From: <span className="font-semibold">{senderName}</span></p>
              )}
            </div>
            {code && (
              <div className="text-right">
                <div className="text-xs opacity-70 mb-1">GIFT CODE</div>
                <div className="font-mono font-bold text-lg tracking-wider bg-white/30 px-3 py-1 rounded backdrop-blur-sm">
                  {code}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Default gradient template
  return (
    <div 
      ref={ref}
      className={`relative w-full aspect-[1.6/1] rounded-2xl bg-gradient-to-br ${template?.bgGradient || 'from-amber-400 to-orange-400'} p-6 shadow-2xl overflow-hidden`}
      style={{ minHeight: '220px' }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-between text-white">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <IconComponent className="h-6 w-6" />
              <span className="text-sm font-medium opacity-90">TOUR FINDER</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">
              {customText?.headline || template?.headline || 'GIFT CARD'}
            </h2>
            <p className="text-lg font-semibold opacity-90 mt-1">
              {customText?.subline || template?.subline || 'Puerto Rico'}
            </p>
          </div>
          {amount && (
            <div className="text-right">
              <div className="text-3xl md:text-4xl font-black">${amount}</div>
              <div className="text-sm opacity-80">Value</div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex justify-between items-end">
          <div>
            {recipientName && (
              <p className="text-sm opacity-80">To: <span className="font-semibold">{recipientName}</span></p>
            )}
            {senderName && (
              <p className="text-sm opacity-80">From: <span className="font-semibold">{senderName}</span></p>
            )}
          </div>
          {code && (
            <div className="text-right">
              <div className="text-xs opacity-70 mb-1">GIFT CODE</div>
              <div className="font-mono font-bold text-lg tracking-wider bg-white/20 px-3 py-1 rounded">
                {code}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Puerto Rico Badge */}
      <div className="absolute bottom-2 right-2 text-xs opacity-60">
        Puerto Rico
      </div>
    </div>
  );
});

GiftCardPreview.displayName = 'GiftCardPreview';

export default function GiftCardsTab({ onNavigateToMessaging }) {
  const [loading, setLoading] = useState(true);
  const [giftCards, setGiftCards] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sendingEmail, setSendingEmail] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(null);
  const [downloadingImage, setDownloadingImage] = useState(false);
  
  // Custom images
  const [customImages, setCustomImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // New card state
  const [selectedTemplate, setSelectedTemplate] = useState(PROMO_TEMPLATES[0]);
  const [selectedCustomImage, setSelectedCustomImage] = useState(null);
  const [useCustomImage, setUseCustomImage] = useState(false);
  const [customText, setCustomText] = useState({ headline: '', subline: '' });
  const [newCard, setNewCard] = useState({
    amount: '',
    sender_name: '',
    recipient_name: '',
    recipient_email: '',
    recipient_phone: '',
    message: ''
  });
  
  // Send channel
  const [sendChannel, setSendChannel] = useState('email'); // 'email', 'sms', 'whatsapp'
  
  // Refs for image export
  const previewRef = useRef(null);
  const dialogPreviewRef = useRef(null);

  useEffect(() => {
    loadGiftCards();
    loadCustomImages();
  }, []);

  const loadGiftCards = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/gift-cards');
      setGiftCards(res.data || []);
    } catch (error) {
      console.error('Error loading gift cards:', error);
      toast.error('Failed to load gift cards');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomImages = async () => {
    try {
      const res = await api.get('/admin/gift-card-images');
      setCustomImages(res.data || []);
    } catch (error) {
      // API might not exist yet, ignore
      console.log('Custom images not loaded');
    }
  };

  // Upload custom image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/admin/gift-card-images/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newImage = res.data;
      setCustomImages(prev => [newImage, ...prev]);
      setSelectedCustomImage(newImage.url);
      setUseCustomImage(true);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Delete custom image
  const deleteCustomImage = async (imageId) => {
    try {
      await api.delete(`/admin/gift-card-images/${imageId}`);
      setCustomImages(prev => prev.filter(img => img.id !== imageId));
      if (selectedCustomImage === customImages.find(img => img.id === imageId)?.url) {
        setSelectedCustomImage(null);
        setUseCustomImage(false);
      }
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  // Download gift card as PNG
  const downloadAsImage = async (elementRef) => {
    if (!elementRef?.current) {
      toast.error('Preview not available');
      return;
    }
    
    setDownloadingImage(true);
    try {
      const canvas = await html2canvas(elementRef.current, {
        scale: 2,
        backgroundColor: null,
        logging: false,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `gift-card-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Gift card image downloaded!');
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to download image');
    } finally {
      setDownloadingImage(false);
    }
  };

  // Send via selected channel
  const sendGiftCard = async (card, channel = sendChannel) => {
    if (channel === 'email') {
      await sendGiftCardEmail(card);
    } else if (channel === 'whatsapp') {
      shareViaWhatsApp(card);
    } else if (channel === 'sms') {
      sendViaSMS(card);
    }
  };

  // Send Gift Card Email
  const sendGiftCardEmail = async (card) => {
    if (!card.recipient_email) {
      toast.error('No recipient email provided');
      return;
    }
    
    setSendingEmail(card.id);
    try {
      const template = PROMO_TEMPLATES.find(t => t.id === card.template_id) || PROMO_TEMPLATES[3];
      const emailSubject = `You've Received a ${template.name} from Tour Finder Puerto Rico!`;
      
      let promoText = '';
      if (template.id === 'free_tour') {
        promoText = 'Enjoy a FREE TOUR on us!';
      } else if (template.id === '50_off') {
        promoText = 'Get 50% OFF your next adventure!';
      } else if (template.id === 'bogo') {
        promoText = 'Buy One Get One FREE on any tour!';
      } else {
        promoText = `Gift Card Value: $${card.amount?.toFixed(2)}`;
      }
      
      const emailBody = `Dear ${card.recipient_name || 'Friend'},

${card.sender_name} has sent you a special gift from Tour Finder Puerto Rico!

${promoText}

Gift Card Code: ${card.code}
${card.amount ? `Value: $${card.amount.toFixed(2)}` : ''}

${card.message ? `Personal Message: "${card.message}"` : ''}

Redeem your gift at tourfinderpr.com and experience the best of Puerto Rico!

From your friends at Tour Finder Puerto Rico`;

      await adminAPI.sendCustomerEmail({
        to_email: card.recipient_email,
        subject: emailSubject,
        body: emailBody,
        template_type: 'gift_card'
      });
      
      toast.success('Gift card email sent!');
    } catch (error) {
      console.error('Error sending gift card email:', error);
      toast.error('Failed to send gift card email');
    } finally {
      setSendingEmail(null);
    }
  };

  // Send via SMS
  const sendViaSMS = (card) => {
    const template = PROMO_TEMPLATES.find(t => t.id === card.template_id) || PROMO_TEMPLATES[3];
    const message = `TOUR FINDER PUERTO RICO - ${template.name}! ${card.sender_name} sent you a gift! Code: ${card.code}${card.amount ? ` Value: $${card.amount.toFixed(2)}` : ''}. Redeem at tourfinderpr.com`;
    
    const phone = card.recipient_phone?.replace(/\D/g, '') || '';
    const smsUrl = `sms:${phone}?body=${encodeURIComponent(message)}`;
    window.open(smsUrl, '_blank');
    toast.success('Opening SMS...');
  };

  // Share via WhatsApp
  const shareViaWhatsApp = (card) => {
    const template = PROMO_TEMPLATES.find(t => t.id === card.template_id) || PROMO_TEMPLATES[3];
    
    const message = `*TOUR FINDER PUERTO RICO*

${card.sender_name} sent you a ${template.name}!

Gift Code: *${card.code}*
${card.amount ? `Value: *$${card.amount.toFixed(2)}*` : ''}

${card.message ? `"${card.message}"` : ''}

Redeem at tourfinderpr.com for amazing Puerto Rico tours!`;

    const phone = card.recipient_phone?.replace(/\D/g, '') || '';
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };

  // Copy to clipboard
  const copyToClipboard = (card) => {
    const template = PROMO_TEMPLATES.find(t => t.id === card.template_id) || PROMO_TEMPLATES[3];
    const text = `TOUR FINDER PUERTO RICO - ${template.name}

Gift Code: ${card.code}
${card.amount ? `Value: $${card.amount.toFixed(2)}` : ''}
${card.recipient_name ? `To: ${card.recipient_name}` : ''}
${card.sender_name ? `From: ${card.sender_name}` : ''}

Redeem at tourfinderpr.com`;

    navigator.clipboard.writeText(text);
    toast.success('Gift card details copied!');
  };

  const createGiftCard = async (sendAfterCreate = false) => {
    if (!newCard.amount || !newCard.sender_name) {
      toast.error('Please fill in required fields');
      return;
    }
    
    if (sendAfterCreate) {
      if (sendChannel === 'email' && !newCard.recipient_email) {
        toast.error('Please enter recipient email');
        return;
      }
      if ((sendChannel === 'sms' || sendChannel === 'whatsapp') && !newCard.recipient_phone) {
        toast.error('Please enter recipient phone number');
        return;
      }
    }
    
    setCreating(true);
    try {
      const res = await api.post('/gift-cards/purchase', {
        ...newCard,
        amount: parseFloat(newCard.amount),
        template_id: useCustomImage ? 'custom' : selectedTemplate.id,
        custom_headline: customText.headline || (useCustomImage ? 'GIFT CARD' : selectedTemplate.headline),
        custom_subline: customText.subline || (useCustomImage ? 'Puerto Rico Tours' : selectedTemplate.subline),
        custom_image_url: useCustomImage ? selectedCustomImage : null
      });
      
      const createdCard = res.data.gift_card;
      toast.success(`Gift card created! Code: ${createdCard.code}`);
      
      // Send if requested
      if (sendAfterCreate) {
        const cardToSend = {
          ...createdCard,
          sender_name: newCard.sender_name,
          recipient_name: newCard.recipient_name,
          recipient_email: newCard.recipient_email,
          recipient_phone: newCard.recipient_phone,
          message: newCard.message,
          template_id: useCustomImage ? 'custom' : selectedTemplate.id
        };
        await sendGiftCard(cardToSend, sendChannel);
      }
      
      setShowCreateDialog(false);
      resetForm();
      loadGiftCards();
    } catch (error) {
      console.error('Error creating gift card:', error);
      toast.error('Failed to create gift card');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNewCard({ amount: '', sender_name: '', recipient_name: '', recipient_email: '', recipient_phone: '', message: '' });
    setCustomText({ headline: '', subline: '' });
    setSelectedTemplate(PROMO_TEMPLATES[0]);
    setSelectedCustomImage(null);
    setUseCustomImage(false);
    setSendChannel('email');
  };

  const filteredCards = giftCards.filter(card => 
    card.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    card.recipient_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    card.sender_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const totalValue = giftCards.reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalBalance = giftCards.reduce((sum, c) => sum + (c.balance || 0), 0);
  const activeCards = giftCards.filter(c => c.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gift-cards-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Gift className="h-6 w-6 text-primary" />
            Gift Cards & Promos
          </h2>
          <p className="text-muted-foreground">Create and share customizable promotional gift cards</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadGiftCards} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Gift Card
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">${totalValue.toFixed(2)}</p>
              <p className="text-sm text-muted-foreground">Total Value</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CreditCard className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">${totalBalance.toFixed(2)}</p>
              <p className="text-sm text-muted-foreground">Remaining Balance</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Gift className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{giftCards.length}</p>
              <p className="text-sm text-muted-foreground">Total Cards</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Check className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{activeCards}</p>
              <p className="text-sm text-muted-foreground">Active Cards</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by code, email, or sender..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Gift Cards List */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Gift className="h-4 w-4" />
          Gift Cards ({filteredCards.length})
        </h3>
        
        <div className="space-y-4">
          {filteredCards.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No gift cards found</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCreateDialog(true)}
              >
                Create your first gift card
              </Button>
            </div>
          ) : (
            filteredCards.map((card) => (
              <div 
                key={card.id}
                className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors gap-4"
              >
                {/* Card Info */}
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${
                    card.template_id === 'free_tour' ? 'from-emerald-500 to-teal-500' :
                    card.template_id === '50_off' ? 'from-orange-500 to-red-500' :
                    card.template_id === 'bogo' ? 'from-purple-500 to-indigo-500' :
                    card.template_id === 'custom' ? 'from-gray-600 to-gray-800' :
                    'from-amber-400 to-orange-400'
                  }`}>
                    <Gift className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-lg">{card.code}</span>
                      <Badge className={card.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                        {card.is_active ? 'Active' : 'Used'}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {card.sender_name || 'Anonymous'}
                        {card.recipient_email && ` → ${card.recipient_email}`}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Calendar className="h-3 w-3" />
                      Created: {new Date(card.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                
                {/* Amount & Balance */}
                <div className="text-right">
                  <p className="text-xl font-bold text-green-600">${card.amount?.toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">
                    Balance: ${card.balance?.toFixed(2) || card.amount?.toFixed(2)}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowPreviewDialog(card)}
                    className="gap-1"
                    title="Preview & Download"
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => sendGiftCardEmail(card)}
                    disabled={sendingEmail === card.id || !card.recipient_email}
                    className="gap-1"
                    title="Send via Email"
                  >
                    {sendingEmail === card.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    Email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => sendViaSMS(card)}
                    className="gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    title="Send via SMS"
                  >
                    <Phone className="h-4 w-4" />
                    SMS
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => shareViaWhatsApp(card)}
                    className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                    title="Send via WhatsApp"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(card)}
                    className="gap-1"
                    title="Copy to clipboard"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Create Gift Card Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              Create Promotional Gift Card
            </DialogTitle>
          </DialogHeader>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: Preview & Templates */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Preview</Label>
              <GiftCardPreview 
                ref={previewRef}
                template={selectedTemplate}
                customText={customText}
                amount={newCard.amount}
                recipientName={newCard.recipient_name}
                senderName={newCard.sender_name}
                code="XXXXXX"
                customImage={useCustomImage ? selectedCustomImage : null}
              />
              
              {/* Download Button */}
              <Button 
                variant="outline" 
                className="w-full gap-2"
                onClick={() => downloadAsImage(previewRef)}
                disabled={downloadingImage}
              >
                {downloadingImage ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download as PNG
              </Button>
              
              {/* Template Selection */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Choose Template</Label>
                <div className="grid grid-cols-5 gap-2">
                  {PROMO_TEMPLATES.map((tpl) => {
                    const Icon = tpl.icon;
                    return (
                      <button
                        key={tpl.id}
                        onClick={() => {
                          setSelectedTemplate(tpl);
                          setUseCustomImage(false);
                          setCustomText({ headline: '', subline: '' });
                        }}
                        className={`p-2 rounded-lg border-2 transition-all ${
                          !useCustomImage && selectedTemplate.id === tpl.id 
                            ? 'border-primary ring-2 ring-primary/20' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        title={tpl.name}
                      >
                        <div className={`w-full aspect-square rounded-md bg-gradient-to-br ${tpl.bgGradient} flex items-center justify-center`}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <p className="text-[10px] mt-1 text-center truncate">{tpl.name}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Custom Images */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">My Images</Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="gap-1"
                  >
                    {uploadingImage ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3" />
                    )}
                    Upload
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                
                {customImages.length > 0 ? (
                  <div className="grid grid-cols-4 gap-2">
                    {customImages.map((img) => (
                      <div key={img.id} className="relative group">
                        <button
                          onClick={() => {
                            setSelectedCustomImage(img.url);
                            setUseCustomImage(true);
                          }}
                          className={`w-full aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                            useCustomImage && selectedCustomImage === img.url
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <img src={img.url} alt="Custom" className="w-full h-full object-cover" />
                        </button>
                        <button
                          onClick={() => deleteCustomImage(img.id)}
                          className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-4 border-2 border-dashed rounded-lg">
                    No custom images yet. Upload one above!
                  </p>
                )}
              </div>
              
              {/* Custom Text */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Customize Text</Label>
                <div>
                  <Label className="text-xs text-muted-foreground">Headline</Label>
                  <Input
                    placeholder={useCustomImage ? 'GIFT CARD' : selectedTemplate.headline}
                    value={customText.headline}
                    onChange={(e) => setCustomText({ ...customText, headline: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Subline</Label>
                  <Input
                    placeholder={useCustomImage ? 'Puerto Rico Tours' : selectedTemplate.subline}
                    value={customText.subline}
                    onChange={(e) => setCustomText({ ...customText, subline: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="space-y-4">
              <div>
                <Label>Amount ($) *</Label>
                <Input
                  type="number"
                  placeholder="100.00"
                  value={newCard.amount}
                  onChange={(e) => setNewCard({ ...newCard, amount: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Sender Name *</Label>
                <Input
                  placeholder="Your name or business"
                  value={newCard.sender_name}
                  onChange={(e) => setNewCard({ ...newCard, sender_name: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Recipient Name</Label>
                <Input
                  placeholder="Recipient's name"
                  value={newCard.recipient_name}
                  onChange={(e) => setNewCard({ ...newCard, recipient_name: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Recipient Email</Label>
                <Input
                  type="email"
                  placeholder="recipient@example.com"
                  value={newCard.recipient_email}
                  onChange={(e) => setNewCard({ ...newCard, recipient_email: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Recipient Phone</Label>
                <Input
                  type="tel"
                  placeholder="+1 (787) 555-1234"
                  value={newCard.recipient_phone}
                  onChange={(e) => setNewCard({ ...newCard, recipient_phone: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Personal Message</Label>
                <Textarea
                  placeholder="Add a personal message..."
                  value={newCard.message}
                  onChange={(e) => setNewCard({ ...newCard, message: e.target.value })}
                  rows={3}
                />
              </div>
              
              {/* Send Channel Selector */}
              <div>
                <Label className="mb-2 block">Send Via</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={sendChannel === 'email' ? 'default' : 'outline'}
                    onClick={() => setSendChannel('email')}
                    className="flex-1 gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </Button>
                  <Button
                    type="button"
                    variant={sendChannel === 'sms' ? 'default' : 'outline'}
                    onClick={() => setSendChannel('sms')}
                    className="flex-1 gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    SMS
                  </Button>
                  <Button
                    type="button"
                    variant={sendChannel === 'whatsapp' ? 'default' : 'outline'}
                    onClick={() => setSendChannel('whatsapp')}
                    className="flex-1 gap-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={() => createGiftCard(false)} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Only
            </Button>
            <Button 
              onClick={() => createGiftCard(true)} 
              disabled={creating || (sendChannel === 'email' ? !newCard.recipient_email : !newCard.recipient_phone)} 
              className="gap-2"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Send className="h-4 w-4" />
              Create & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview/Download Dialog */}
      <Dialog open={!!showPreviewDialog} onOpenChange={() => setShowPreviewDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Gift Card Preview
            </DialogTitle>
          </DialogHeader>
          
          {showPreviewDialog && (
            <div className="space-y-4">
              <GiftCardPreview 
                ref={dialogPreviewRef}
                template={PROMO_TEMPLATES.find(t => t.id === showPreviewDialog.template_id) || PROMO_TEMPLATES[3]}
                customText={{ 
                  headline: showPreviewDialog.custom_headline || '', 
                  subline: showPreviewDialog.custom_subline || '' 
                }}
                amount={showPreviewDialog.amount}
                recipientName={showPreviewDialog.recipient_name}
                senderName={showPreviewDialog.sender_name}
                code={showPreviewDialog.code}
                customImage={showPreviewDialog.custom_image_url}
              />
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Code:</span>
                  <span className="font-mono font-bold ml-2">{showPreviewDialog.code}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <Badge className={`ml-2 ${showPreviewDialog.is_active ? 'bg-green-500' : 'bg-gray-500'}`}>
                    {showPreviewDialog.is_active ? 'Active' : 'Used'}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Value:</span>
                  <span className="font-bold text-green-600 ml-2">${showPreviewDialog.amount?.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Balance:</span>
                  <span className="font-bold ml-2">${showPreviewDialog.balance?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => downloadAsImage(dialogPreviewRef)}
              disabled={downloadingImage}
              className="gap-2"
            >
              {downloadingImage ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Download PNG
            </Button>
            <Button variant="outline" onClick={() => setShowPreviewDialog(null)}>
              Close
            </Button>
            <Button 
              onClick={() => { copyToClipboard(showPreviewDialog); }}
              variant="outline"
              className="gap-2"
            >
              <Share2 className="h-4 w-4" />
              Copy
            </Button>
            <Button 
              onClick={() => { shareViaWhatsApp(showPreviewDialog); }}
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
